import { Controller } from "@hotwired/stimulus"
import { logEvent, logWarn } from "../field_logger"

import { batteryMonitoringEnabled, batteryMonitoringAvailable, batteryApiAvailable } from "../battery_monitor"
import { recordSettingChange } from "../field_settings_sync"
import { getCameraCaptureMode, recommendedCameraCaptureMode } from "../lib/camera_capture_preference"
import { MISSION_SORT_STORAGE_KEY } from "../lib/place_mission_order"

const SORT_KEY    = "field_places_sort"
const FILTER_KEY  = "field_places_filter"
const VIEW_KEY    = "field_view_field_places"
const GEO_KEY     = "field_user_location"
const GEO_DENIED_KEY = "field_geolocation_denied_until"

const SORT_LABELS = {
  name:           "Name",
  visit_priority: "Priority",
  retailer:       "Retailer",
  distance:       "Distance",
}

const DIR_LABELS = {
  asc:  "A → Z",
  desc: "Z → A",
}

// Place page mission sort (card #4098)
const MISSION_SORT_LABELS = {
  priority:    "Priority",
  name:        "Name",
  schedule_at: "Scheduled date",
}

const MISSION_DIR_LABELS = {
  asc:  "Ascending",
  desc: "Descending",
}

// Connects to data-controller="field-settings"
// Reads localStorage preferences and displays them for the user.
export default class extends Controller {
  static targets = [
    "locationStatus", "locationBadge", "locationBtn", "locationDetail", "locationCoords", "locationAge",
    "cameraStatus", "cameraBadge",
    "placesSort", "placesView", "placesFilter", "clearFiltersBtn", "missionsSort",
    "batteryRow", "batteryLabel", "batteryHint", "batteryTrack", "batteryKnob", "batteryStatus",
    "cameraModeTrack", "cameraModeKnob", "cameraModeStatus",
  ]

  connect() {
    this._renderLocation()
    this._renderCamera()
    this._renderSort()
    this._renderMissionsSort()
    this._renderView()
    this._renderFilters()
    this._renderBattery()
    this._renderCameraMode()
    this._logGeolocationPermission()

    // A settings pull (head office changed it) re-renders an open Settings page.
    this._batteryChanged = () => this._renderBattery()
    this._cameraModeChanged = () => this._renderCameraMode()
    window.addEventListener("field:battery-monitoring-changed", this._batteryChanged)
    window.addEventListener("field:camera-capture-mode-changed", this._cameraModeChanged)
  }

  disconnect() {
    window.removeEventListener("field:battery-monitoring-changed", this._batteryChanged)
    window.removeEventListener("field:camera-capture-mode-changed", this._cameraModeChanged)
  }

  requestLocation() {
    if (!("geolocation" in navigator)) {
      this.locationStatusTarget.textContent = "Not supported on this device"
      return
    }

    this.locationStatusTarget.textContent = "Requesting..."
    this.locationBtnTarget.classList.add("hidden")
    const start = performance.now()

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const elapsed = Math.round(performance.now() - start)
        const data = {
          lat:       pos.coords.latitude,
          lng:       pos.coords.longitude,
          accuracy:  pos.coords.accuracy,
          timestamp: Date.now(),
        }
        localStorage.setItem(GEO_KEY, JSON.stringify(data))
        localStorage.removeItem(GEO_DENIED_KEY)
        logEvent("geolocation", "Location acquired (manual)", {
          accuracy: Math.round(pos.coords.accuracy), elapsed
        }).catch(() => {})
        this._renderLocation()
      },
      (err) => {
        const elapsed = Math.round(performance.now() - start)
        const reason = err.code === err.PERMISSION_DENIED ? "denied"
                     : err.code === err.TIMEOUT           ? "timeout"
                     :                                      "unavailable"
        logWarn("geolocation", `Location failed: ${reason} (manual)`, { code: err.code, elapsed }).catch(() => {})
        if (err.code === err.PERMISSION_DENIED) {
          this.locationStatusTarget.textContent = "Permission denied — enable location in browser settings"
          this.locationBadgeTarget.textContent  = "Blocked"
          this.locationBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-500"
        } else {
          this.locationStatusTarget.textContent = "Could not get location — try again"
          this.locationBtnTarget.classList.remove("hidden")
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  clearFilters() {
    localStorage.removeItem(FILTER_KEY)
    this._renderFilters()
  }

  resetPreferences() {
    localStorage.removeItem(SORT_KEY)
    localStorage.removeItem(MISSION_SORT_STORAGE_KEY)
    localStorage.removeItem(FILTER_KEY)
    localStorage.removeItem(VIEW_KEY)
    this._renderSort()
    this._renderMissionsSort()
    this._renderView()
    this._renderFilters()
  }

  _renderLocation() {
    const loc = this._read(GEO_KEY)
    if (!loc) {
      this.locationStatusTarget.textContent = "Not yet obtained"
      this.locationBadgeTarget.textContent  = "Off"
      this.locationBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-400"
      this.locationBtnTarget.classList.remove("hidden")
      this.locationBtnTarget.textContent = "Enable"
      this.locationDetailTarget.style.display = "none"
      return
    }

    const ageMs   = Date.now() - loc.timestamp
    const ageMins = Math.round(ageMs / 60000)
    const ageText = ageMins < 1 ? "just now" : ageMins === 1 ? "1 min ago" : `${ageMins} mins ago`

    this.locationStatusTarget.textContent   = `Last obtained ${ageText}`
    this.locationBadgeTarget.textContent    = "Active"
    this.locationBadgeTarget.className      = "text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-600"
    this.locationBtnTarget.classList.remove("hidden")
    this.locationBtnTarget.textContent      = "Update"
    this.locationDetailTarget.style.display = "block"
    this.locationCoordsTarget.textContent   = `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`
    this.locationAgeTarget.textContent      = `Accuracy: ±${Math.round(loc.accuracy)}m`
  }

  _renderCamera() {
    if (!this.hasCameraStatusTarget) return
    if (!("permissions" in navigator)) {
      this.cameraStatusTarget.textContent = "Permissions API not supported"
      this.cameraBadgeTarget.textContent  = "Unknown"
      this.cameraBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-400"
      return
    }
    navigator.permissions.query({ name: "camera" }).then(result => {
      if (result.state === "granted") {
        this.cameraStatusTarget.textContent = "Camera access granted"
        this.cameraBadgeTarget.textContent  = "Active"
        this.cameraBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-600"
      } else if (result.state === "denied") {
        this.cameraStatusTarget.textContent = "Permission denied — enable camera in browser settings"
        this.cameraBadgeTarget.textContent  = "Blocked"
        this.cameraBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-500"
      } else {
        this.cameraStatusTarget.textContent = "Not yet requested"
        this.cameraBadgeTarget.textContent  = "Off"
        this.cameraBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-400"
      }
      // Proactive state telemetry -- fires on connect() and again on every
      // onchange, unlike the reactive fallback logging that only fires when an
      // actual capture request fails. Gives us camera permission state even for
      // taskers who never trip the fallback path.
      logEvent("camera", "Permission state", { state: result.state }).catch(() => {})
      result.onchange = () => this._renderCamera()
    }).catch(() => {
      this.cameraStatusTarget.textContent = "Could not query camera permission"
      this.cameraBadgeTarget.textContent  = "Unknown"
      this.cameraBadgeTarget.className    = "text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-400"
    })
  }

  // Proactive geolocation permission state, logged once on connect (and left to
  // the browser's own onchange-free lifecycle -- unlike camera, this page has no
  // geolocation badge to re-render on change, so we only need the one read).
  // Pure telemetry side-effect: does NOT touch _renderLocation, which handles
  // cached last-known-location display, a different concern.
  _logGeolocationPermission() {
    if (!("permissions" in navigator)) return
    navigator.permissions.query({ name: "geolocation" }).then(result => {
      logEvent("geolocation", "Permission state", { state: result.state }).catch(() => {})
    }).catch(() => {})
  }

  _renderSort() {
    const sort = this._read(SORT_KEY)
    if (!sort) {
      this.placesSortTarget.textContent = "Default (name, A → Z)"
      return
    }
    const field = SORT_LABELS[sort.sortBy] || sort.sortBy
    const dir   = DIR_LABELS[sort.sortDir] || sort.sortDir
    this.placesSortTarget.textContent = `${field} — ${dir}`
  }

  _renderMissionsSort() {
    if (!this.hasMissionsSortTarget) return
    const sort = this._read(MISSION_SORT_STORAGE_KEY)
    if (!sort) {
      this.missionsSortTarget.textContent = "Default (priority, then name)"
      return
    }
    const field = MISSION_SORT_LABELS[sort.sortBy] || sort.sortBy
    const dir   = MISSION_DIR_LABELS[sort.sortDir] || sort.sortDir
    this.missionsSortTarget.textContent = `${field}, ${dir.toLowerCase()}`
  }

  _renderView() {
    const view = localStorage.getItem(VIEW_KEY)
    this.placesViewTarget.textContent = view === "map" ? "Map" : "List"
  }

  _renderFilters() {
    const filter = this._read(FILTER_KEY)
    const active = filter && Object.values(filter).some(v => v)

    if (!active) {
      this.placesFilterTarget.textContent = "None active"
      this.clearFiltersBtnTarget.classList.add("hidden")
      return
    }

    const parts = []
    if (filter.retailer_id)     parts.push("Retailer")
    if (filter.place_owner_id)  parts.push("Owner")
    if (filter.visit_frequency) parts.push("Visit frequency")

    this.placesFilterTarget.textContent = parts.join(", ")
    this.clearFiltersBtnTarget.classList.remove("hidden")
  }

  toggleBattery() {
    const from = batteryMonitoringEnabled()
    const to = !from
    // Stores the choice and its time on the phone, queues it for the server
    // (card #4153) and sends it now when online.
    recordSettingChange("battery_monitoring", to)
    logEvent("battery", "Battery monitoring changed", { from, to }).catch(() => {})
    // The layout's field-battery controller starts or stops the monitor, and
    // the iPhone prompt shows or hides.
    window.dispatchEvent(new CustomEvent("field:battery-monitoring-changed"))
    this._renderBattery()
  }

  // The row only means something where there is a Battery API (Android) or an
  // iPhone to ask, so it stays hidden anywhere else. On iPhone "on" means the
  // 30 minute battery check-in, and the default is off; on Android it is the
  // background monitor, on by default. A pull from the server (a head office
  // change) re-renders it.
  _renderBattery() {
    if (this.hasBatteryRowTarget) {
      this.batteryRowTarget.classList.toggle("hidden", !batteryMonitoringAvailable())
    }

    const checkIns = !batteryApiAvailable()
    if (this.hasBatteryLabelTarget) {
      this.batteryLabelTarget.textContent = checkIns ? "Battery check-ins" : "Battery monitoring"
    }
    if (this.hasBatteryHintTarget) {
      this.batteryHintTarget.classList.toggle("hidden", !checkIns)
    }

    const enabled = batteryMonitoringEnabled()

    if (this.hasBatteryTrackTarget) {
      if (enabled) {
        this.batteryTrackTarget.classList.remove("bg-slate-200")
        this.batteryTrackTarget.classList.add("bg-sapphire")
        this.batteryKnobTarget.classList.add("translate-x-4")
      } else {
        this.batteryTrackTarget.classList.add("bg-slate-200")
        this.batteryTrackTarget.classList.remove("bg-sapphire")
        this.batteryKnobTarget.classList.remove("translate-x-4")
      }
    }

    if (this.hasBatteryStatusTarget) {
      this.batteryStatusTarget.textContent = enabled ? "On" : "Off"
    }
  }

  // Camera capture mode toggle -- see camera_capture_preference.js. Mirrors
  // toggleBattery()/_renderBattery() exactly, just flipping between "in-app"
  // and "native" instead of a boolean.
  toggleCameraMode() {
    const from = getCameraCaptureMode()
    const next = from === "native" ? "in-app" : "native"
    // Stores the choice and its time on the phone, queues it for the server
    // (card #4153) and sends it now when online. Only an explicit toggle goes
    // to the server: the phone's stored value alone may just be the
    // device-detected recommendation.
    recordSettingChange("camera_capture_mode", next)
    logEvent("camera", "Capture mode changed", { from, to: next }).catch(() => {})
    this._renderCameraMode()
  }

  _renderCameraMode() {
    const mode = getCameraCaptureMode()
    const isNative = mode === "native"
    const matchesRecommended = mode === recommendedCameraCaptureMode()

    if (this.hasCameraModeTrackTarget) {
      if (isNative) {
        this.cameraModeTrackTarget.classList.remove("bg-slate-200")
        this.cameraModeTrackTarget.classList.add("bg-sapphire")
        this.cameraModeKnobTarget.classList.add("translate-x-4")
      } else {
        this.cameraModeTrackTarget.classList.add("bg-slate-200")
        this.cameraModeTrackTarget.classList.remove("bg-sapphire")
        this.cameraModeKnobTarget.classList.remove("translate-x-4")
      }
    }

    if (this.hasCameraModeStatusTarget) {
      let status
      if (isNative) {
        status = matchesRecommended
          ? "Native camera app (recommended for this device)"
          : "Native camera app (your device's in-app camera already captures full quality)"
      } else {
        status = matchesRecommended
          ? "In-app camera (recommended for this device)"
          : "In-app camera (native available if you'd prefer it)"
      }
      this.cameraModeStatusTarget.textContent = status
    }
  }

  _read(key) {
    try { return JSON.parse(localStorage.getItem(key)) } catch { return null }
  }
}
